import React, { ReactNode } from 'react'

import { TOPBAR_HEIGHT } from 'components_new/templates/PageLayout'

import Box from 'components_new/atoms/Box'
import Divider from 'components_new/atoms/Divider'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Text from 'components_new/atoms/Text'

import { OptionProps } from 'components_new/molecules/OptionsMenu'
import OptionsMenuButton from 'components_new/molecules/OptionsMenuButton'
import Breadcrumbs, {
  TBreadcrumbItem
} from 'components_new/molecules/Breadcrumbs'

import ProfileAvatar from 'components_new/organisms/ProfileAvatar'

import SidebarButton from 'components_new/templates/PageLayout/SidebarButton'

import { useHistory } from 'react-router-dom'
import { useIsMobile } from 'utils/hooks'
import { useTranslation } from 'language/translator'
import { useTheme } from '@mui/material'

interface TopbarProps {
  actions?: ReactNode
  breadcrumbs?: TBreadcrumbItem[]
  children?: ReactNode
  header?: ReactNode
  mobile?: {
    goBack?: boolean
  }
  navLocked?: boolean
  noGutter?: boolean
  options?: OptionProps[]
  rightSidebar?: {
    open: boolean
    toggleOpen: (value: boolean) => void
  }
  sx?: object
  title?: ReactNode
}

const Topbar = (props: TopbarProps) => {
  const {
    actions,
    breadcrumbs,
    children,
    mobile,
    noGutter,
    options,
    rightSidebar,
    sx,
    title
  } = props

  const { t } = useTranslation()
  const history = useHistory()
  const isMobile = useIsMobile()
  const theme = useTheme()

  return (
    <Box
      component="header"
      sx={{
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        flexShrink: 0,
        height: TOPBAR_HEIGHT,
        left: 0,
        position: 'sticky',
        right: 0,
        top: 0,
        zIndex: theme.zIndex.appBar,
        width: '100%',
        ...sx
      }}
    >
      <Box
        sx={{
          bottom: 0,
          display: 'flex',
          left: 0,
          right: 0,
          position: 'absolute',
          top: 0
        }}
      >
        <Box
          sx={{
            p: noGutter ? 0 : 1,
            flex: '1 1 auto',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            minWidth: 0
          }}
        >
          {isMobile && mobile?.goBack ? (
            <IconButton
              color="primary"
              onClick={() => history.goBack()}
              size="small"
            >
              <Icon fontSize="small" name="ArrowBackIos" />
            </IconButton>
          ) : null}

          {breadcrumbs ? (
            <Breadcrumbs
              highlightCurrent={true}
              items={breadcrumbs}
              sx={{ ml: isMobile && mobile?.goBack ? 0 : 0.75 }}
            />
          ) : null}

          {title ? (
            <Box sx={{ cursor: 'default', ml: 3, mr: 2 }}>
              <Text variant="navigation">{title}</Text>
            </Box>
          ) : null}

          {children}
        </Box>

        {actions || options || rightSidebar || isMobile ? (
          <Box
            sx={{
              p: 1,
              pr: 2,
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flex: 0
            }}
          >
            {actions}

            {options && options.length > 0 && !isMobile ? (
              <>
                <Divider flexItem={true} orientation="vertical" />
                <OptionsMenuButton
                  MenuProps={{
                    maxHeight: 480
                  }}
                  options={options}
                  size="small"
                  tooltip={t('global', 'options')}
                />
              </>
            ) : null}

            {rightSidebar && !isMobile ? (
              <>
                <Divider flexItem={true} orientation="vertical" />
                <Box>
                  <SidebarButton
                    open={rightSidebar.open}
                    toggleOpen={rightSidebar.toggleOpen}
                    variant="right"
                  />
                </Box>
              </>
            ) : null}

            {isMobile ? (
              <>
                <Divider flexItem={true} orientation="vertical" />
                <ProfileAvatar
                  sx={{
                    flex: '0 0 auto',
                    ['& .ListItemText']: {
                      display: 'none'
                    }
                  }}
                />
              </>
            ) : null}
          </Box>
        ) : null}
      </Box>
    </Box>
  )
}

export default Topbar
